import React, { useState, useCallback, useEffect, useMemo, Dispatch, SetStateAction } from "react";
import {
  Modal,
  Table,
  Form,
  Button,
  Divider,
  Icon,
  Popup,
  Search,
  Message,
  Label,
  Checkbox,
  Dropdown,
} from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import { UserChip } from "components/lib/Chips";
import AnnouncementBase from "./Base";
import styled from "styled-components";
import toast from "react-hot-toast";
import api from "api";
import util from "utils/utils";
import { useAppSelector } from "store";
import { ChallengeChooser, UserChooser } from "components/lib/Choosers";
import { StyledModal } from "../StyledModal";
import { OpenAPI, Other } from "simplydo/interfaces";

import {
  availableConditions,
  conditionChecks,
  getDefaultCondition,
  renderValueCheck,
} from "components/ideas/Body/Custom/fieldCondition";

const AnnouncementFromContainer = styled.div`
  margin-top: 20px;
  > span {
    display: block;
    margin: 2.5px 0;
  }
  > .from-title {
    font-weight: bold;
  }
  > .from-info {
    color: gray;
    font-size: 0.9em;
  }
  .ui.search {
    margin: 5px 0;
  }
  .chosen-user-container {
    display: flex;
    gap: 5px;
    align-items: center;
    margin-top: 10px;
  }
  .search-results {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    span {
      display: block;
      font-weight: bold;
    }
    img {
      height: 30px;
      width: 30px;
      border-radius: 50%;
      margin-right: 5px;
    }
  }
`;

type AutomaticConditions = {
  value: string;
  text: string;
  subConditions?: string;
  getOptions?: (challengeId: string, callback: (options: any) => void) => void;
};

type AutomaticAnnouncement = OpenAPI.GET<"/{entity}/{id}/announcements">["response"]["automaticAnnouncements"][0];

type AutomaticAnnouncementsManagerProps = {
  forType: string;
  forId: string;
  automaticAnnouncements: AutomaticAnnouncement[];
  setAutomaticAnnouncements: Dispatch<SetStateAction<AutomaticAnnouncement[]>>;
  automaticConditions: AutomaticConditions[];
  templatesAvailable?: boolean;
  setTemplateModalOpen: Dispatch<SetStateAction<boolean>>;
  handleCreatedAnnouncement: (announcement: AutomaticAnnouncement, openEditor?: boolean) => void;
  deleteAnnouncement: (announcementId: string) => void;
  externalOpenEditor?: AutomaticAnnouncement;
  setExternalOpenEditor: Dispatch<SetStateAction<AutomaticAnnouncement>>;
};

const AutomaticAnnouncementsManager = ({
  forType,
  forId,
  automaticAnnouncements,
  setAutomaticAnnouncements,
  automaticConditions,
  templatesAvailable,
  setTemplateModalOpen,
  handleCreatedAnnouncement,
  deleteAnnouncement,
  externalOpenEditor,
  setExternalOpenEditor,
}: AutomaticAnnouncementsManagerProps) => {
  const user = useAppSelector((state) => state.user);
  const [subconditionOptions, setSubconditionOptions] = useState<{ ideaFields?: any }>({});
  const { t } = useTranslation();

  const [editingAutomaticAnnouncement, setEditingAutomaticAnnouncement] = useState<Partial<AutomaticAnnouncement>>({});
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fromUserValue, setFromUserValue] = useState("");
  const [fromUsersState, setFromUsersState] = useState({
    users: [],
    loading: false,
  });
  const [selectedFromUser, setSelectedFromUser] = useState(null);

  const [userHasChallenges, setUserHasChallenges] = useState(false);
  const [challengeChooserOpen, setChallengeChooserOpen] = useState(false);
  const [copyingFromChallenge, setCopyingFromChallenge] = useState(false);

  const userId = user?._id;
  const userIsAdmin = util.hasPermission(user, "org.viewDashboard", user?.organisation?._id);
  const orgId = user?.organisation?._id;

  const createAutomatic = useCallback(() => {
    setSending(true);
    const {
      sendWhen,
      sendWhenConditions,
      subject,
      body,
      text,
      replyTo,
      callToActionText,
      callToActionLink,
      fullScreen,
      customRecipientsEnabled,
      calendarEvent,
    } = editingAutomaticAnnouncement;
    api.announcements.create(
      forType,
      forId,
      {
        type: "automatic",
        sendWhen,
        sendWhenConditions,
        subject,
        body,
        text,
        callToActionText,
        callToActionLink,
        fromUser: selectedFromUser?._id,
        replyTo,
        fullScreen,
        customRecipients: editingAutomaticAnnouncement?.customRecipients,
        customRecipientsEnabled,
        calendarEvent,
      },
      (announcement) => {
        handleCreatedAnnouncement({ ...announcement, ownerFromUser: selectedFromUser });
        setSending(false);
        setEditingAutomaticAnnouncement({});
        setExternalOpenEditor(null);
      },
      (err) => {
        toast.error(err.message);
        setSending(false);
      },
    );
  }, [
    forType,
    forId,
    editingAutomaticAnnouncement,
    selectedFromUser,
    handleCreatedAnnouncement,
    setExternalOpenEditor,
  ]);

  const getSendWhenText = useCallback(
    (announcementSendWhen) => {
      const condition = automaticConditions.find((autoCondition) => autoCondition.value === announcementSendWhen);
      return condition?.text;
    },
    [automaticConditions],
  );

  const getSubconditions = useCallback(
    (condition) => automaticConditions.find((sc) => sc.value === condition)?.subConditions,
    [automaticConditions],
  );

  const saveAutomaticEditing = useCallback(() => {
    setSaving(true);
    api.announcements.update(
      forType,
      forId,
      editingAutomaticAnnouncement._id,
      {
        recipientConditions: editingAutomaticAnnouncement.recipientConditions,
        fromUser: selectedFromUser?._id || null,
        subject: editingAutomaticAnnouncement.subject,
        body: editingAutomaticAnnouncement.body,
        text: editingAutomaticAnnouncement.text,
        sendWhen: editingAutomaticAnnouncement.sendWhen,
        sendWhenConditions: editingAutomaticAnnouncement.sendWhenConditions,
        replyTo: editingAutomaticAnnouncement.replyTo,
        callToActionText: editingAutomaticAnnouncement.callToActionText,
        callToActionLink: editingAutomaticAnnouncement.callToActionLink,
        fullScreen: editingAutomaticAnnouncement.fullScreen,
        customRecipients: editingAutomaticAnnouncement?.customRecipients,
        customRecipientsEnabled: editingAutomaticAnnouncement?.customRecipientsEnabled,
        calendarEvent: editingAutomaticAnnouncement?.calendarEvent,
      },
      (announcement) => {
        const updatedAutomaticAnnouncements = Object.assign([], automaticAnnouncements).map((a) => {
          if (a._id === announcement._id) return { ...announcement, ownerFromUser: selectedFromUser };
          return a;
        });
        setAutomaticAnnouncements(updatedAutomaticAnnouncements);
        setSaving(false);
        setEditingAutomaticAnnouncement({});
        setExternalOpenEditor(null);
        toast.success(t("generic.saved", { type: "Announcement" }));
      },
      (err) => {
        toast.error(err.message);
        setSaving(false);
      },
    );
  }, [
    t,
    forType,
    forId,
    selectedFromUser,
    editingAutomaticAnnouncement,
    automaticAnnouncements,
    setAutomaticAnnouncements,
    setExternalOpenEditor,
  ]);

  const deleteAutomaticAnnouncement = useCallback(
    (announcementId) => {
      setEditingAutomaticAnnouncement({});
      deleteAnnouncement(announcementId);
    },
    [deleteAnnouncement],
  );

  const getFromUsers = useCallback(() => {
    if (fromUserValue) {
      setFromUsersState((prevState) => ({ ...prevState, loading: true }));
      if (util.hasPermission(user, "org.listUsers", orgId)) {
        api.organisations.getUsers(
          orgId,
          { page: 1, view: "all", query: fromUserValue },
          (data) => {
            setFromUsersState((prevState) => ({
              ...prevState,
              users: data.users,
              loading: false,
            }));
          },
          () => {
            setFromUsersState((prevState) => ({ ...prevState, loading: false }));
          },
        );
      } else {
        api.organisations.getAdmins(
          orgId,
          { limit: 10, permissions: ["org.viewDashboard"] },
          ({ users }) => {
            setFromUsersState((prevState) => ({
              ...prevState,
              users,
              loading: false,
            }));
          },
          () => {
            setFromUsersState((prevState) => ({ ...prevState, loading: false }));
          },
        );
      }
    } else {
      setFromUsersState((prevState) => ({ ...prevState, loading: false }));
    }
  }, [orgId, fromUserValue, user]);

  useEffect(() => getFromUsers(), [getFromUsers]);

  const copyAnnouncementsFromChallenge = useCallback(
    (challenge: Other.IChallenge) => {
      setCopyingFromChallenge(true);
      api.announcements.copy(
        {
          fromEntity: "challenge",
          fromEntityId: challenge._id,
          fromEntityType: "automatic",
          toEntity: forType,
          toEntityId: forId,
          toEntityType: "automatic",
        },
        (data) => {
          setAutomaticAnnouncements((prevState) => [...data.announcements, ...prevState]);
          toast.success(`Copied announcements from "${challenge.name}"`);
          setCopyingFromChallenge(false);
          setChallengeChooserOpen(false);
        },
        (err) => {
          toast.error(err.message);
          setCopyingFromChallenge(false);
        },
      );
    },
    [forId, forType, setAutomaticAnnouncements],
  );

  const fromUserResults = useMemo(
    () =>
      fromUsersState.users.map((u) => ({
        id: u._id,
        title: u.profile.fullName,
        image: util.avatarUrl(u),
        user: u,
      })),
    [fromUsersState.users],
  );

  useEffect(() => {
    if (externalOpenEditor) {
      setEditingAutomaticAnnouncement(externalOpenEditor);
    }
  }, [externalOpenEditor]);

  useEffect(() => {
    api.users.getChallenges(
      userId,
      { page: 1, limit: 1, query: "" },
      (data) => {
        setUserHasChallenges(data.challenges.length > 0);
      },
      () => {},
    );
  }, [userId]);

  // We set this if we have any data from Sendgrid about automatic announcements. This comes in the form of an object "events"
  // With possible event counts: openCount, deliveredCount, bounceCount
  const autoAnnouncementsHaveEventData = useMemo(
    () => !!automaticAnnouncements.find((a) => !!a.events),
    [automaticAnnouncements],
  );

  const FromUserSelector = useMemo(() => {
    if (userIsAdmin) {
      return (
        <AnnouncementFromContainer>
          <span className="from-title">Message sender</span>
          <span className="from-info">
            When your automatic announcement is sent to users, it will appear as a direct message. By default, that
            direct message will come from your account. If you want to change this, please search for a user below.
          </span>

          <Search
            style={{ marginTop: 5 }}
            placeholder="Search for a user.."
            loading={fromUsersState.loading}
            onSearchChange={(e, { value }) => setFromUserValue(value)}
            value={fromUserValue}
            results={fromUserResults}
            onResultSelect={(e, { result }) => setSelectedFromUser(result.user)}
            resultRenderer={(result) => (
              <div className="search-results">
                <img src={result.image} alt="User avatar" />
                <span>{result.title}</span>
              </div>
            )}
          />
          {selectedFromUser ? (
            <div className="chosen-user-container">
              <UserChip openInTab user={selectedFromUser} />
              <Button size="mini" icon="trash" basic onClick={() => setSelectedFromUser(null)} />
            </div>
          ) : null}
        </AnnouncementFromContainer>
      );
    }
    return null;
  }, [userIsAdmin, fromUsersState, fromUserValue, selectedFromUser, fromUserResults]);

  useEffect(() => {
    const cond = automaticConditions.find((sc) => sc.value === editingAutomaticAnnouncement.sendWhen);
    if (cond && cond.subConditions && cond.getOptions && !subconditionOptions[cond.subConditions]) {
      cond.getOptions(forId, (options) =>
        setSubconditionOptions((prev) => ({
          ...prev,
          [cond.subConditions]: options,
        })),
      );
    }
  }, [forId, subconditionOptions, automaticConditions, editingAutomaticAnnouncement.sendWhen]);

  const customRecipientselector = useMemo(() => {
    if (userIsAdmin) {
      return (
        <AnnouncementFromContainer>
          <span className="from-title">Message receiver</span>
          <span className="from-info">
            When your announcement is triggered the announcement will be sent to the user that triggered the
            announcement. For example, if a an announcement is sent on submission of an idea, the user that submitted
            the idea will be contacted. If you want to change this, please search for recipients below.
          </span>
          <Dropdown
            text={
              !editingAutomaticAnnouncement?.customRecipientsEnabled
                ? "User that triggered the condition"
                : "Select custom users"
            }
            selection
          >
            <Dropdown.Menu>
              <Dropdown.Item
                onClick={() =>
                  setEditingAutomaticAnnouncement((prevState) => ({ ...prevState, customRecipientsEnabled: false }))
                }
              >
                User that triggered the condition.
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() =>
                  setEditingAutomaticAnnouncement((prevState) => ({ ...prevState, customRecipientsEnabled: true }))
                }
              >
                Select custom users:
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          {editingAutomaticAnnouncement?.customRecipientsEnabled && (
            <>
              <UserChooser
                selectedUsers={editingAutomaticAnnouncement.customRecipientOwners || []}
                trigger={<Button style={{ marginTop: 5, marginLeft: 10 }} icon="plus" content="Add custom recipient" />}
                // @ts-ignore
                enabledFeatures={{ search: true }}
                searchFunction={(query, success, failure) => {
                  if (!query) {
                    success([]);
                    return;
                  }
                  api.search.omni(
                    {
                      query,
                      include: ["users"],
                    },
                    ({ users }) => users && success(users),
                    failure,
                  );
                }}
                onComplete={(users: OpenAPI.Schemas["User"][]) => {
                  if (users.length === 0) {
                    return;
                  }
                  setEditingAutomaticAnnouncement((prevState) => ({
                    ...prevState,
                    customRecipientOwners: users,
                    customRecipients: users.map((u) => u._id),
                  }));
                }}
              />
              {editingAutomaticAnnouncement?.customRecipientOwners?.length !== 0 ? (
                <Table>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>User</Table.HeaderCell>
                      <Table.HeaderCell></Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {editingAutomaticAnnouncement?.customRecipientOwners?.map((recipient) => (
                      <Table.Row key={recipient._id}>
                        <Table.Cell>
                          <UserChip openInTab user={recipient} />
                        </Table.Cell>
                        <Table.Cell collapsing>
                          <Button
                            icon="trash"
                            basic
                            content="Remove"
                            color="red"
                            onClick={() =>
                              setEditingAutomaticAnnouncement((prevState) => ({
                                ...prevState,
                                customRecipients: prevState.customRecipients.filter((id) => id !== recipient._id),
                                customRecipientOwners: prevState.customRecipientOwners.filter(
                                  (u) => u._id !== recipient._id,
                                ),
                              }))
                            }
                          />
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              ) : null}
            </>
          )}
        </AnnouncementFromContainer>
      );
    }
    return null;
  }, [userIsAdmin, editingAutomaticAnnouncement]);

  return (
    <div>
      <ChallengeChooser
        controlled
        isOpen={challengeChooserOpen}
        onClose={() => setChallengeChooserOpen(false)}
        onComplete={(challenge) => copyAnnouncementsFromChallenge(challenge)}
        ignores={[forId]}
        instructions="Choose a challenge to copy announcements from. You can view, edit and delete them after copying."
        buttonProps={{
          loading: copyingFromChallenge,
          content: "Copy from this challenge",
        }}
        trigger={undefined}
        createNewAction={undefined}
      />

      <h3>
        {!automaticAnnouncements.length && <Icon name={"exclamation circle"} color={"red"} />}
        {t("announcements.automatic.title")}
      </h3>
      <p>{t("announcements.automatic.info")}</p>

      <StyledModal
        open={!util.isEmpty(editingAutomaticAnnouncement)}
        onClose={() => setEditingAutomaticAnnouncement({})}
      >
        <Modal.Header>
          {editingAutomaticAnnouncement._id ? (
            t("announcements.automatic.edit.title")
          ) : (
            <>
              {t("announcements.automatic.new.title")}
              <br />
              <small>{t("announcements.manual.sendInfo")}</small>
            </>
          )}
        </Modal.Header>
        <Modal.Content>
          <Modal.Description>
            <AnnouncementBase
              announcement={editingAutomaticAnnouncement}
              setAnnouncement={setEditingAutomaticAnnouncement}
              pre={
                <>
                  {editingAutomaticAnnouncement.countSent ? (
                    <p>This announcement has been sent {editingAutomaticAnnouncement.countSent || 0} times.</p>
                  ) : null}
                  <Message negative={!editingAutomaticAnnouncement.sendWhen}>
                    <Message.Header>Automation rule (required)</Message.Header>
                    Please choose which condition must be met to send this announcement.
                    <Divider hidden />
                    <Form.Select
                      label={t("announcements.automatic.list.sendWhen")}
                      placeholder="Select an event trigger"
                      options={automaticConditions.map((c) => ({ key: c.value, text: t(c.text), value: c.value }))}
                      value={editingAutomaticAnnouncement.sendWhen}
                      onChange={(e, { value }) => {
                        setEditingAutomaticAnnouncement((prevState) => ({ ...prevState, sendWhen: value as string }));
                      }}
                    />
                    {getSubconditions(editingAutomaticAnnouncement.sendWhen) === "ideaFields" ? (
                      <>
                        <p>
                          Only send this announcement if{" "}
                          <strong>
                            <Dropdown
                              inline
                              value={editingAutomaticAnnouncement.sendWhenConditions?.conditionType ?? "any"}
                              options={[
                                { key: "any", text: "any of", value: "any" },
                                { key: "all", text: "all of", value: "all" },
                              ]}
                              onChange={(event, { value }) => {
                                setEditingAutomaticAnnouncement((prevState) => ({
                                  ...prevState,
                                  sendWhenConditions: {
                                    ...(prevState.sendWhenConditions || {}),
                                    conditionType: value as "any" | "all",
                                  },
                                }));
                              }}
                            />
                          </strong>{" "}
                          the following conditions are met:
                        </p>
                        {editingAutomaticAnnouncement.sendWhenConditions?.conditions?.map((condition, i) => (
                          <Form.Group widths="equal" key={`${i}-${condition.field}-${condition.condition}`}>
                            <Form.Field>
                              {i === 0 ? <label>1. Field with name</label> : null}
                              <Form.Dropdown
                                placholder="Select a field"
                                clearable
                                selection
                                value={condition.field}
                                options={subconditionOptions.ideaFields
                                  ?.filter((f) => f.type in conditionChecks)
                                  ?.map((f) => ({
                                    key: f.id,
                                    description: f.section,
                                    text: f.title,
                                    value: f.id,
                                  }))}
                                onChange={(e, { value }) => {
                                  setEditingAutomaticAnnouncement((prevState) => ({
                                    ...prevState,
                                    sendWhenConditions: {
                                      ...(prevState.sendWhenConditions || {}),
                                      conditions: prevState.sendWhenConditions?.conditions?.map((c, j) => {
                                        if (i === j) {
                                          return {
                                            ...c,
                                            field: value as string,
                                            condition: getDefaultCondition(
                                              subconditionOptions.ideaFields?.find((f) => f.id === value)?.type,
                                            ),
                                            value: null,
                                          };
                                        }
                                        return c;
                                      }),
                                    },
                                  }));
                                }}
                              />
                            </Form.Field>
                            <Form.Field>
                              {i === 0 ? <label>2. Meets condition</label> : null}
                              <Form.Dropdown
                                placholder="Select a condition"
                                selection
                                value={condition.condition}
                                options={
                                  availableConditions[
                                    subconditionOptions.ideaFields?.find(
                                      (chosenField) => chosenField.id === condition.field,
                                    )?.type
                                  ]
                                }
                                onChange={(e, { value }) => {
                                  setEditingAutomaticAnnouncement((prevState) => ({
                                    ...prevState,
                                    sendWhenConditions: {
                                      ...(prevState.sendWhenConditions || {}),
                                      conditions: prevState.sendWhenConditions?.conditions?.map((c, j) => {
                                        if (i === j) {
                                          return {
                                            ...c,
                                            condition: value as
                                              | "isEmpty"
                                              | "isNotEmpty"
                                              | "equalTo"
                                              | "includes"
                                              | "doesNotInclude",
                                            value: null,
                                          };
                                        }
                                        return c;
                                      }),
                                    },
                                  }));
                                }}
                              />
                            </Form.Field>
                            <Form.Field>
                              {i === 0 ? <label>&nbsp;</label> : null}
                              {renderValueCheck(
                                condition,
                                subconditionOptions.ideaFields?.find(
                                  (chosenField) => chosenField.id === condition.field,
                                ),
                                (value) => {
                                  setEditingAutomaticAnnouncement((prevState) => ({
                                    ...prevState,
                                    sendWhenConditions: {
                                      ...(prevState.sendWhenConditions || {}),
                                      conditions: prevState.sendWhenConditions?.conditions?.map((c, j) => {
                                        if (i === j) {
                                          return {
                                            ...c,
                                            value,
                                          };
                                        }
                                        return c;
                                      }),
                                    },
                                  }));
                                },
                              )}
                            </Form.Field>
                            <Button
                              style={{ marginTop: i === 0 ? 25 : 0 }}
                              icon="trash"
                              onClick={() =>
                                setEditingAutomaticAnnouncement((prevState) => ({
                                  ...prevState,
                                  sendWhenConditions: {
                                    ...(prevState.sendWhenConditions || {}),
                                    conditions: prevState.sendWhenConditions?.conditions?.filter((c, j) => j !== i),
                                  },
                                }))
                              }
                            />
                          </Form.Group>
                        ))}
                        <Form.Group>
                          <Form.Button
                            onClick={() => {
                              setEditingAutomaticAnnouncement((prevState) => ({
                                ...prevState,
                                sendWhenConditions: {
                                  ...(prevState.sendWhenConditions || {}),
                                  conditions: [
                                    ...(prevState.sendWhenConditions?.conditions ?? []),
                                    {
                                      field: null,
                                      condition: "isNotEmpty",
                                      value: null,
                                    },
                                  ],
                                },
                              }));
                            }}
                          >
                            Add a condition
                          </Form.Button>
                        </Form.Group>
                      </>
                    ) : null}
                  </Message>
                  <Message>
                    <Message.Header>
                      Enable "full-screen" announcement <Label color="teal" content="New!" />
                    </Message.Header>
                    <Divider hidden />
                    <Checkbox
                      toggle
                      label='If enabled, SimplyDo will also show this announcement in "full-screen mode" for the recipient, increasing the chance that they read it at the point of delivery.'
                      checked={editingAutomaticAnnouncement.fullScreen}
                      onChange={(e, { checked }) =>
                        setEditingAutomaticAnnouncement((prevState) => ({ ...prevState, fullScreen: checked }))
                      }
                    />
                  </Message>
                </>
              }
              post={
                <>
                  {FromUserSelector}
                  {customRecipientselector}
                </>
              }
            />
          </Modal.Description>
        </Modal.Content>
        <Modal.Actions>
          <Button content={t("generic.cancel")} onClick={() => setEditingAutomaticAnnouncement({})} />
          {editingAutomaticAnnouncement._id ? (
            <Button
              secondary
              icon="save"
              content={t("generic.saveChanges")}
              onClick={saveAutomaticEditing}
              loading={saving}
            />
          ) : (
            <Button
              secondary
              icon="plus"
              content={t("generic.create")}
              onClick={createAutomatic}
              loading={sending}
              disabled={!editingAutomaticAnnouncement.sendWhen}
            />
          )}
        </Modal.Actions>
        {editingAutomaticAnnouncement._id ? (
          <Modal.Actions>
            <h4>{t("announcements.delete.title")}</h4>
            <Button
              onClick={() => deleteAutomaticAnnouncement(editingAutomaticAnnouncement._id)}
              content={t("generic.delete")}
              icon="trash"
              basic
              color="red"
              size="small"
            />
          </Modal.Actions>
        ) : null}
      </StyledModal>

      <div style={{ display: "flex", justifyContent: "flex-end", margin: "10px 0" }}>
        {userHasChallenges && forType === "challenge" ? (
          <Button
            secondary
            icon="copy"
            content="Copy announcements from another challenge"
            size="small"
            onClick={() => setChallengeChooserOpen(true)}
          />
        ) : null}
        {templatesAvailable ? (
          <Button
            secondary
            icon="copy"
            content="View announcement templates"
            size="small"
            onClick={() => setTemplateModalOpen(true)}
          />
        ) : null}
        <Button
          size="small"
          secondary
          icon="paper plane"
          content="New auto announcement"
          onClick={() => setEditingAutomaticAnnouncement({ _id: null })}
        />
      </div>

      {automaticAnnouncements.length === 0 ? null : (
        <div>
          <h5 style={{ clear: "both" }}>Current auto announcements</h5>
          <Table unstackable>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Send when</Table.HeaderCell>
                <Table.HeaderCell>Subject</Table.HeaderCell>
                {autoAnnouncementsHaveEventData ? (
                  [
                    <Table.HeaderCell key="sent-header" collapsing>
                      Sent
                    </Table.HeaderCell>,
                    <Table.HeaderCell key="delivered-header" collapsing>
                      Delivered
                    </Table.HeaderCell>,
                    <Table.HeaderCell key="bounced-header" collapsing>
                      Bounced{" "}
                      <Popup
                        content="Bounced emails were rejected by the recipient's mail server. This is commonly caused by a misspelled or nonexistent email address."
                        trigger={<Icon name="question circle" />}
                      />
                    </Table.HeaderCell>,
                    <Table.HeaderCell key="opened-header" collapsing>
                      Opened
                    </Table.HeaderCell>,
                  ]
                ) : (
                  <Table.HeaderCell collapsing>Times sent</Table.HeaderCell>
                )}
                <Table.HeaderCell />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {automaticAnnouncements.map((a) => (
                <Table.Row key={a._id}>
                  <Table.Cell collapsing>{t(getSendWhenText(a.sendWhen))}</Table.Cell>
                  <Table.Cell>{a.subject}</Table.Cell>
                  {autoAnnouncementsHaveEventData ? (
                    [
                      <Table.Cell key={`${a._id}-sent`} collapsing>
                        {a.events?.processedCount || a.countSent || 0}
                      </Table.Cell>,
                      <Table.Cell key={`${a._id}-delivered`} collapsing>
                        {a.events?.deliveredCount || 0}
                      </Table.Cell>,
                      <Table.Cell key={`${a._id}-bounced`} collapsing>
                        {a.events?.bounceCount || 0}
                      </Table.Cell>,
                      <Table.Cell key={`${a._id}-opened`} collapsing>
                        {a.events?.openCount || 0}
                      </Table.Cell>,
                    ]
                  ) : (
                    <Table.Cell collapsing>{a.countSent || "Never"}</Table.Cell>
                  )}
                  <Table.Cell collapsing>
                    <Button
                      basic
                      size="tiny"
                      icon="edit"
                      content="Edit"
                      onClick={() => {
                        setEditingAutomaticAnnouncement(a);
                        setSelectedFromUser(a.ownerFromUser);
                      }}
                    />
                    <Button basic size="tiny" icon="trash" onClick={() => deleteAutomaticAnnouncement(a._id)} />
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      )}
    </div>
  );
};

export default AutomaticAnnouncementsManager;
